import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { formatDateString } from "utilities/date";


// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${TableCell.head}`]: {
    backgroundColor: "#FEF0DE",
    fontSize: 16,
    fontWeight: "bold",
  },
  [`&.${TableCell.body}`]: {
    fontSize: 16,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const BookingTable = ({ bookings, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, onUpdateStatus }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const handleClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedBookingId(id);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    navigate(`/booking-details/${selectedBookingId}`);
    handleClose();
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return '#ff9800'; // Orange
      case 'CONFIRMED':
        return '#4caf50'; // Green
      case 'CANCELLED':
        return '#f44336'; // Red
      case 'COMPLETED':
        return '#2196f3'; // Blue
      default:
        return '#757575'; // Grey
    }
  };

  const formatVisitDateTime = (date, time) => {
    if (!date) return 'N/A';
    try {
      const dateObj = new Date(date);
      const formattedDate = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      if (time) {
        // Format time from HH:MM:SS to HH:MM
        const timeStr = time.substring(0, 5);
        return `${formattedDate} at ${timeStr}`;
      }
      return formattedDate;
    } catch (error) {
      return date || 'N/A';
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow style={{ backgroundColor: '#FEF0DE'  }}>
            <StyledTableCell style={{ fontSize: "18px" }}>Visitor Name</StyledTableCell>
            <StyledTableCell style={{ fontSize: "18px" }}>Email</StyledTableCell>
            <StyledTableCell style={{ fontSize: "18px" }}>Phone Number</StyledTableCell>
            <StyledTableCell style={{ fontSize: "18px" }}>Property</StyledTableCell>
            <StyledTableCell style={{ fontSize: "18px" }}>Unit</StyledTableCell>
            <StyledTableCell style={{ fontSize: "18px" }}>Visit Date & Time</StyledTableCell>
            <StyledTableCell style={{ fontSize: "18px" }}>Status</StyledTableCell>
            <StyledTableCell style={{ fontSize: "18px" }}>Actions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings && bookings.length > 0 ? (
            bookings
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((booking, index) => (
                <StyledTableRow key={booking.id} style={{ backgroundColor: index % 2 === 0 ? '#f5f5f5' : 'inherit' }}>
                  <StyledTableCell style={{ fontSize: "16px" }}>{booking.visitor_name || 'N/A'}</StyledTableCell>
                  <StyledTableCell style={{ fontSize: "16px" }}>{booking.email || 'N/A'}</StyledTableCell>
                  <StyledTableCell style={{ fontSize: "16px" }}>{booking.phone_number || 'N/A'}</StyledTableCell>
                  <StyledTableCell style={{ fontSize: "16px" }}>
                    {booking.related_rental_unit?.related_rental?.rental_name || 'N/A'}
                  </StyledTableCell>
                  <StyledTableCell style={{ fontSize: "16px" }}>
                    {booking.related_rental_unit?.unit_name || 'N/A'}
                  </StyledTableCell>
                  <StyledTableCell style={{ fontSize: "16px" }}>
                    {formatVisitDateTime(booking.desired_visit_date, booking.desired_visit_time)}
                  </StyledTableCell>
                  <StyledTableCell style={{ fontSize: "16px" }}>
                    <span style={{ 
                      color: getStatusColor(booking.status),
                      fontWeight: 'bold',
                      textTransform: 'capitalize'
                    }}>
                      {booking.status || 'N/A'}
                    </span>
                  </StyledTableCell>
                  <StyledTableCell style={{ fontSize: "16px" }}>
                    <IconButton
                      onClick={(event) => handleClick(event, booking.id)}
                      color="primary"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </StyledTableCell>
                </StyledTableRow>
              ))
          ) : (
            <StyledTableRow>
              <StyledTableCell colSpan={8} align="center" style={{ fontSize: "16px", padding: "20px" }}>
                No bookings found
              </StyledTableCell>
            </StyledTableRow>
          )}
        </TableBody>
      </Table>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
        <MenuItem onClick={() => {
          if (onUpdateStatus) {
            onUpdateStatus(selectedBookingId);
          }
          handleClose();
        }}>Update Status</MenuItem>
      </Menu>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={bookings?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
    </TableContainer>
  );
};

export default BookingTable;
